// src/pages/league-dashboard/team-home/StartingPitchers.js
//
// Extracted from inline definition in TeamHomeDashboard.js per Tier 2
// #9 commit 9c (refactor only — no behavior change).
import React from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { dynastyTheme } from '../../../services/colorService';
import PlayerLink from '../../../components/PlayerLink';

const StartingPitchers = ({ starters, rosterPlayers = [] }) => {
  const safeGames = Array.isArray(starters) ? starters : [];
  const safeRosterPlayers = Array.isArray(rosterPlayers) ? rosterPlayers : [];
  const [selectedDay, setSelectedDay] = React.useState(0);
  const [faOnly, setFaOnly] = React.useState(false);

  // Only CURRENT-roster players count as "yours". Cut players are re-injected into
  // the canonical roster as status='former' (build_roster_response); excluding
  // former/inactive (the sibling PlayerNotes filter) keeps a dropped pitcher from
  // being marked "YOURS" on the upcoming-starters tile.
  const rosterPlayerIds = new Set(
    safeRosterPlayers
      .filter(p => {
        const status = p.league_context?.roster?.status;
        return status !== 'former' && status !== 'inactive';
      })
      .map(p => p.ids?.mlb)
      .filter(Boolean)
  );

  // Group games by date, preserve order
  const gamesByDate = React.useMemo(() => {
    const map = {};
    safeGames.forEach(game => {
      const date = game.game_date || 'Unknown';
      if (!map[date]) map[date] = [];
      map[date].push(game);
    });
    return map;
  }, [safeGames]);

  const dates = Object.keys(gamesByDate).sort();
  const activeDateGames = gamesByDate[dates[selectedDay]] || [];

  // FA filter: keep games where at least one pitcher is a FA
  const visibleGames = faOnly
    ? activeDateGames.filter(game =>
        game.away_pitcher?.is_free_agent || game.home_pitcher?.is_free_agent
      )
    : activeDateGames;

  const getPitcherStatus = (pitcher) => {
    if (!pitcher) return 'none';
    if (pitcher.is_free_agent) return 'fa';
    if (rosterPlayerIds.has(pitcher.mlb_id)) return 'yours';
    return 'owned';
  };

  const statusStyles = {
    yours: {
      name: `font-bold ${dynastyTheme.classes.text.primary}`,
      badge: 'bg-yellow-500/20 text-yellow-400 border border-yellow-500/30',
      label: 'YOURS'
    },
    fa: {
      name: `font-bold text-green-400`,
      badge: 'bg-green-500/20 text-green-400 border border-green-500/30',
      label: 'FA'
    },
    owned: {
      name: `font-medium ${dynastyTheme.classes.text.neutralLight}`,
      badge: 'bg-neutral-700 text-neutral-400 border border-neutral-600',
      label: null
    },
    none: {
      name: `italic ${dynastyTheme.classes.text.neutralLighter}`,
      badge: null,
      label: null
    }
  };

  // Format innings pitched: 20.333 → "20.1"
  const formatIP = (ip) => {
    if (ip == null) return null;
    const full = Math.floor(ip);
    const frac = Math.round((ip - full) * 3);
    return `${full}.${frac}`;
  };

  const buildStatLine = (pitcher) => {
    if (!pitcher || pitcher.era == null) return null;
    const parts = [];
    if (pitcher.wins != null && pitcher.losses != null) parts.push(`${pitcher.wins}-${pitcher.losses}`);
    if (pitcher.era != null) parts.push(`${pitcher.era.toFixed(2)} ERA`);
    if (pitcher.whip != null) parts.push(`${pitcher.whip.toFixed(2)} WHIP`);
    if (pitcher.innings_pitched != null) parts.push(`${formatIP(pitcher.innings_pitched)} IP`);
    if (pitcher.strikeouts_pitched != null) parts.push(`${pitcher.strikeouts_pitched} K`);
    return parts.length > 0 ? parts.join(' · ') : null;
  };

  const PitcherSide = ({ pitcher, mlbTeam, align = 'left' }) => {
    if (!pitcher || !pitcher.mlb_id) {
      return (
        <div className={`flex-1 ${align === 'right' ? 'text-right' : 'text-left'}`} style={{ opacity: 0.45 }}>
          <span className={`text-xs italic ${dynastyTheme.classes.text.neutralLighter}`}>TBD</span>
          <div className={`text-xs ${dynastyTheme.classes.text.neutralLighter} mt-0.5`}>{mlbTeam}</div>
        </div>
      );
    }
    const status = getPitcherStatus(pitcher);
    const styles = statusStyles[status];
    const isRight = align === 'right';
    const statLine = buildStatLine(pitcher);

    return (
      <div className={`flex-1 ${isRight ? 'text-right' : 'text-left'}`}>
        <span className={`text-sm ${styles.name} truncate`}>
          <PlayerLink
            mlbId={pitcher.mlb_id}
            name={pitcher.name}
            style={{ fontSize: 'inherit', fontWeight: 'inherit', color: 'inherit' }}
          />
        </span>
        <div className={`text-xs ${dynastyTheme.classes.text.neutralLighter} mt-0.5`}>
          {mlbTeam}
        </div>
        {statLine && (
          <div className={`text-[10px] ${dynastyTheme.classes.text.neutralLighter} mt-0.5 leading-tight`}>
            {statLine}
          </div>
        )}
        <div className="flex items-center gap-1 mt-1 flex-wrap" style={{ justifyContent: isRight ? 'flex-end' : 'flex-start' }}>
          {styles.label && (
            <span className={`text-xs px-1.5 py-0 rounded font-bold ${styles.badge}`}>
              {styles.label}
            </span>
          )}
          {status === 'owned' && pitcher.team_name && (
            <span className={`text-xs px-1.5 py-0 rounded ${styles.badge}`}>
              {pitcher.team_name}
            </span>
          )}
        </div>
      </div>
    );
  };

  if (!safeGames.length) {
    return (
      <div className={dynastyTheme.components.card.base}>
        <div className="p-3">
          <h3 className={dynastyTheme.components.heading.h3}>⚾ Today's Matchups</h3>
          <div className={`${dynastyTheme.classes.text.neutralLight} text-center py-8`}>
            <p className="text-sm">No probable pitchers available</p>
          </div>
        </div>
      </div>
    );
  }

  // Build date label for the selected day
  const getDateLabel = (index) => {
    if (index === 0) return 'Today';
    if (index === 1) return 'Tomorrow';
    const dateStr = dates[index];
    if (!dateStr) return '';
    return new Date(dateStr + 'T12:00:00').toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
  };

  return (
    <div className={dynastyTheme.components.card.base}>
      <div className="p-3">
        {/* Header with date nav */}
        <div className="flex items-center justify-between mb-3">
          <h3 className={dynastyTheme.components.heading.h3}>⚾ Matchups</h3>
          <button
            onClick={() => setFaOnly(v => !v)}
            className={`text-xs px-2 py-1 rounded border transition-colors ${
              faOnly
                ? 'bg-green-500/20 text-green-400 border-green-500/30'
                : `${dynastyTheme.classes.text.neutralLight} border-neutral-600 hover:border-neutral-400`
            }`}
          >
            FA Only
          </button>
        </div>

        {/* Date navigation: < Today > */}
        {dates.length > 1 && (
          <div className="flex items-center justify-center gap-3 mb-3">
            <button
              onClick={() => setSelectedDay(d => Math.max(0, d - 1))}
              disabled={selectedDay === 0}
              className={`p-1 rounded transition-colors ${
                selectedDay === 0
                  ? 'opacity-0 pointer-events-none'
                  : `${dynastyTheme.classes.text.neutralLight} hover:${dynastyTheme.classes.text.white}`
              }`}
            >
              <ChevronLeft size={16} />
            </button>
            <span className={`text-sm font-semibold ${dynastyTheme.classes.text.white} min-w-[120px] text-center`}>
              {getDateLabel(selectedDay)}
            </span>
            <button
              onClick={() => setSelectedDay(d => Math.min(dates.length - 1, d + 1))}
              disabled={selectedDay >= dates.length - 1}
              className={`p-1 rounded transition-colors ${
                selectedDay >= dates.length - 1
                  ? 'opacity-0 pointer-events-none'
                  : `${dynastyTheme.classes.text.neutralLight} hover:${dynastyTheme.classes.text.white}`
              }`}
            >
              <ChevronRight size={16} />
            </button>
          </div>
        )}

        {/* Matchup rows */}
        <div className="space-y-2 max-h-80 overflow-y-auto">
          {visibleGames.length === 0 ? (
            <div className={`text-xs text-center py-4 ${dynastyTheme.classes.text.neutralLighter}`}>
              {faOnly ? 'No matchups with available free agents' : 'No games scheduled'}
            </div>
          ) : (
            visibleGames.map((game, index) => (
              <div
                key={game.game_id || index}
                className={`flex items-center gap-2 p-2 rounded-lg border ${dynastyTheme.classes.border.neutral} bg-neutral-800/50`}
              >
                <PitcherSide pitcher={game.away_pitcher} mlbTeam={game.away_team} align="left" />
                <div className="flex flex-col items-center flex-shrink-0 px-1">
                  <span className={`text-xs font-bold ${dynastyTheme.classes.text.neutralLighter}`}>@</span>
                  <span className={`text-xs ${dynastyTheme.classes.text.neutralLighter} whitespace-nowrap`}>
                    {game.game_time || '--'}
                  </span>
                </div>
                <PitcherSide pitcher={game.home_pitcher} mlbTeam={game.home_team} align="right" />
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
};

export default StartingPitchers;
